<template>
  <div class="sidebar-content flex" :class="dynamicClass">
    <div
      v-show="hasDesktopSidebarOpened && !hasMobile"
      class="sidebar-content-sidebar"
      :class="sidebarContentSidebarClasses"
    >
      <slot name="sidebar" />
    </div>
    <div class="sidebar-content-content relative w-full flex-1">
      <slot />
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  type OpenedSidebar,
  OpenedSidebarPosition,
} from "@zaprooecommerce/storefront-ui-vue";
import type { SidebarContentInstance } from "~/modules/helpers/components/SidebarContent/types";

const { openSidebar: openZaprooSidebar } = useZaprooSidebar();
const { isDown, getNumberSize } = useViewportObserver();
const slots = useSlots();

const props = defineProps<{
  opened?: boolean;
  mobileSidebarPosition?: OpenedSidebarPosition;
  sidebarContentSidebarClasses?: string;
  mobileSidebarTitle?: string;
  mobileSidebarClasses?: string;
  mobileSidebarContentClasses?: string;
  mobileSidebarHeadingClasses?: string;
  mobileViewMaxWidth?: Breakpoint | number;
}>();

const openedMobileSidebar = shallowRef<OpenedSidebar | undefined>(undefined);

const hasMobile = computed<boolean>(() =>
  isDown(props.mobileViewMaxWidth ?? Breakpoint.lg),
);
const stringifyMobileViewMaxWidth = computed<string>(
  () => getNumberSize(props.mobileViewMaxWidth ?? Breakpoint.lg) + "px",
);
const dynamicClass = computed(
  () => `sidebar-media-width-${stringifyMobileViewMaxWidth.value}`,
);

const hasMobileSidebarOpened = computed<boolean>(
  () => !!openedMobileSidebar.value,
);
const hasDesktopSidebarOpened = ref<boolean>(true);
const hasAnySidebarOpened = computed<boolean>(
  () =>
    hasMobileSidebarOpened.value ||
    (hasDesktopSidebarOpened.value && !hasMobile.value),
);

function openSidebar(): void {
  if (hasMobile.value) {
    const openedSidebar = openZaprooSidebar(
      defineAsyncComponent(
        () => import("../../sidebars/SidebarContentSidebar.vue"),
      ),
      {
        position: props.mobileSidebarPosition,
        payload: {
          contentSlot: slots.sidebar,
          sidebarTitle: props.mobileSidebarTitle,
          customClasses: props.mobileSidebarClasses,
          sidebarContentClasses: props.mobileSidebarContentClasses,
          sidebarHeadingClasses: props.mobileSidebarHeadingClasses,
        } as any,
      },
    );
    openedMobileSidebar.value = markRaw(openedSidebar);
  } else {
    hasDesktopSidebarOpened.value = true;
  }
}

function closeSidebar(): void {
  if (!hasMobile.value) {
    hasDesktopSidebarOpened.value = false;
  }
  closeZaprooSidebar();
}

function closeZaprooSidebar(): void {
  openedMobileSidebar.value?.close();
  openedMobileSidebar.value = undefined;
}

useHead({
  style: computed(() => [
    {
      innerHTML: `
        .sidebar-content.${dynamicClass.value} {
          @media not all and (min-width: ${stringifyMobileViewMaxWidth.value}) {
            .sidebar-content-sidebar {
              display: none;
            }
          }
        }
      `,
    },
  ]),
});

onUnmounted(() => {
  closeZaprooSidebar();
});

defineExpose({
  openSidebar,
  closeSidebar,
  hasMobile,
  hasMobileSidebarOpened,
  hasDesktopSidebarOpened,
  hasAnySidebarOpened,
} as SidebarContentInstance);
</script>
<style lang="scss">
.sidebar-content {
  &-sidebar {
    @apply mr-10 h-max py-1 md:w-[375px];
    box-shadow: 4px 4px 28px 0 rgba(5, 41, 85, 0.07);
  }
}
</style>
